/**
 * Pure, React-free grouping for the employee-blocked PTO table — the same shape
 * the timecard surface uses (see `timecardGrouping.ts`), kept separate from the
 * component so the ordering and the at-a-glance totals stay directly testable.
 *
 * SECURITY / PII: touches only fields the PTO API already returns (employee id +
 * display name, dates, hours, derived status). Nothing sensitive is derived here.
 */

import type { PtoEntry, PtoStatus } from '@worklogix/shared';

/**
 * Count + hours for one status bucket within an employee's block.
 *
 * `hours` is the PAYABLE figure — a booking reconciled down because the employee
 * clocked time that day contributes its reduced hours, not what was requested.
 * The at-a-glance header therefore shows what payroll will actually see.
 */
export interface PtoBucket {
  count: number;
  hours: number;
}

/**
 * An employee's PTO at a glance. `upcoming` is the number that matters most on
 * this screen — it is what answers "does this person already have time booked
 * ahead?" without opening anything.
 */
export interface PtoBlockTotals {
  /** Booked but not started (status `pending`). */
  upcoming: PtoBucket;
  /** Currently within the booked range. */
  active: PtoBucket;
  /** Finished. */
  complete: PtoBucket;
  /** All buckets combined. */
  total: PtoBucket;
}

/** One employee's block: their entries plus the at-a-glance totals. */
export interface PtoEmployeeBlock {
  employeeId: string;
  employeeName: string;
  entries: PtoEntry[];
  totals: PtoBlockTotals;
}

const emptyBucket = (): PtoBucket => ({ count: 0, hours: 0 });

/** Bucket an entry lands in. `new` never reaches here — it is client-only, pre-save. */
function bucketFor(status: PtoStatus): keyof Omit<PtoBlockTotals, 'total'> | null {
  switch (status) {
    case 'pending':
      return 'upcoming';
    case 'active':
      return 'active';
    case 'complete':
      return 'complete';
    default:
      return null;
  }
}

/**
 * Group entries into per-employee blocks.
 *
 * Blocks are ordered by employee name; WITHIN a block the newest start date comes
 * first, so an employee's upcoming time off sits at the top of their block where
 * it is seen — which is the whole point of blocking the table this way.
 *
 * Does not mutate the input.
 */
export function groupPtoByEmployee(entries: readonly PtoEntry[]): PtoEmployeeBlock[] {
  const byEmployee = new Map<string, PtoEntry[]>();
  for (const entry of entries) {
    const existing = byEmployee.get(entry.employeeId);
    if (existing === undefined) {
      byEmployee.set(entry.employeeId, [entry]);
    } else {
      existing.push(entry);
    }
  }

  const blocks: PtoEmployeeBlock[] = [];
  for (const [employeeId, rows] of byEmployee) {
    const sorted = [...rows].sort((a, b) => {
      if (a.startDate !== b.startDate) {
        return a.startDate < b.startDate ? 1 : -1; // newest range first
      }
      return a.id < b.id ? -1 : a.id > b.id ? 1 : 0; // stable tiebreak
    });

    const totals: PtoBlockTotals = {
      upcoming: emptyBucket(),
      active: emptyBucket(),
      complete: emptyBucket(),
      total: emptyBucket(),
    };
    for (const row of sorted) {
      const key = bucketFor(row.status);
      const payable = row.reconciliation.effectiveHours;
      if (key !== null) {
        totals[key].count += 1;
        totals[key].hours += payable;
      }
      totals.total.count += 1;
      totals.total.hours += payable;
    }

    for (const bucket of [totals.upcoming, totals.active, totals.complete, totals.total]) {
      bucket.hours = Math.round(bucket.hours * 100) / 100;
    }

    blocks.push({
      employeeId,
      employeeName: sorted[0]?.employeeName ?? '',
      entries: sorted,
      totals,
    });
  }

  return blocks.sort((a, b) =>
    a.employeeName.localeCompare(b.employeeName, undefined, { sensitivity: 'base' })
  );
}
